import React, { useState } from "react";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Briefcase,
  FileText,
  Shield,
  CreditCard,
  ArrowLeft,
  Globe,
  Flag,
  CreditCard as Bank,
  Building,
  Hash,
  Check,
  Clock,
  Edit,
  Save,
  X,
  Download,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { adminUserService } from "@/services/api/admin.user.service";
import UserReportsTab from "../report/UserReportsTab";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

const UserDetailView = ({ userData, onBack, onUpdate }) => {
  // Extract user and kyc data from the response data structure
  const { user, kyc } = userData.data;

  // State for edit mode
  const [isEditing, setIsEditing] = useState(false);
  // State for loading during API calls
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success"); // success, error

  const [showReportModal, setShowReportModal] = useState(false);

  // State for editable fields
  const [editableFields, setEditableFields] = useState({
    email: user.email || "",
    mobileNo: user.mobileNo || "",
    fullName: user.fullName || "",
    status: user.status || "inactive",
    isEmailVerified: user.isEmailVerified || false,
    isMobileVerified: user.isMobileVerified || false,
    isKycVerified: user.isKycVerified || false,
  });

  // Toast notification function
  const showToastNotification = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);

    // Auto-hide toast after 4 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const documentStatusBadge = (doc) => {
    if (!doc) return null;
    return (
      <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
        {doc.uploadedAt ? formatDate(doc.uploadedAt) : "Uploaded"}
      </span>
    );
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditableFields({
      ...editableFields,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Use the service to update the user
      if (onUpdate) {
        // Use the callback if provided
        await onUpdate(user._id, editableFields);
      } else {
        // Use the service method instead of direct API call
        await adminUserService.updateUser(user._id, editableFields);
      }

      // Show success toast
      showToastNotification(
        "User information updated successfully!",
        "success"
      );

      // Exit edit mode
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating user:", error);
      // Show error toast
      showToastNotification(
        error.message || "Failed to update user information",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openReportModal = () => {
    setShowReportModal(true);
  };

  const closeReportModal = () => {
    setShowReportModal(false);
  };

  // Handler for report download success
  const handleReportDownloadSuccess = (
    message = "Report downloaded successfully!"
  ) => {
    showToastNotification(message, "success");
  };

  // Handler for report download error
  const handleReportDownloadError = (
    errorMessage = "Failed to download report. Please try again."
  ) => {
    showToastNotification(errorMessage, "error");
  };

  const cancelEdit = () => {
    // Reset to original values
    setEditableFields({
      email: user.email || "",
      mobileNo: user.mobileNo || "",
      fullName: user.fullName || "",
      status: user.status || "inactive",
      isEmailVerified: user.isEmailVerified || false,
      isMobileVerified: user.isMobileVerified || false,
      isKycVerified: user.isKycVerified || false,
    });
    setIsEditing(false);
  };

  return (
    <>
      <Button onClick={onBack} variant="outlined">
        Back to List
      </Button>

      <div className="overflow-hidden max-w-6xl mx-auto p-4">
        {/* Toast Notification */}
        {showToast && (
          <div className="fixed bottom-4 right-4 z-50 animate-slide-in-right">
            <div
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg shadow-lg min-w-80 ${
                toastType === "success"
                  ? "bg-green-500 text-white"
                  : "bg-red-500 text-white"
              }`}
            >
              <div className="flex items-center space-x-2 flex-1">
                {toastType === "success" ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span className="text-sm font-medium">{toastMessage}</span>
              </div>
              <button
                onClick={() => setShowToast(false)}
                className="text-white hover:text-gray-200 focus:outline-none"
                aria-label="Close notification"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* User profile header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center">
            <div className="h-16 w-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-medium">
              {editableFields.fullName
                ?.split(" ")
                .map((n) => n[0])
                .join("") || "U"}
            </div>
            <div className="mt-3 sm:mt-0 sm:ml-4">
              {isEditing ? (
                <TextField
                  name="fullName"
                  value={editableFields.fullName}
                  onChange={handleInputChange}
                  variant="standard"
                  className="text-xl font-semibold text-gray-800"
                />
              ) : (
                <h3 className="text-xl font-semibold text-gray-800">
                  {editableFields.fullName || "N/A"}
                </h3>
              )}
              <div className="flex items-center mt-1 text-gray-600">
                <Mail size={16} className="mr-1" />
                {isEditing ? (
                  <TextField
                    name="email"
                    value={editableFields.email}
                    onChange={handleInputChange}
                    variant="standard"
                  />
                ) : (
                  <span>{editableFields.email || "N/A"}</span>
                )}
              </div>
            </div>
            <div className="mt-3 sm:mt-0 sm:ml-auto">
              {isEditing ? (
                <Select
                  name="status"
                  value={editableFields.status}
                  onChange={handleInputChange}
                  size="small"
                  className="rounded-full"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              ) : (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    editableFields.status === "active"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {editableFields.status || "Inactive"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* User information sections */}
        <div className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Personal Information */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-800 mb-4">
                Personal Information dd
              </h4>
              <div className="space-y-3">
                <div className="flex items-start">
                  <User
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">Full Name</p>
                    {isEditing ? (
                      <TextField
                        name="fullName"
                        value={editableFields.fullName}
                        onChange={handleInputChange}
                        variant="standard"
                        fullWidth
                      />
                    ) : (
                      <p className="text-gray-800">
                        {editableFields.fullName || "N/A"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-start">
                  <Phone
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">Phone Number</p>
                    {isEditing ? (
                      <TextField
                        name="mobileNo"
                        value={editableFields.mobileNo}
                        onChange={handleInputChange}
                        variant="standard"
                        fullWidth
                      />
                    ) : (
                      <p className="text-gray-800">
                        {editableFields.mobileNo || "Not provided"}
                      </p>
                    )}
                    <div className="text-xs text-gray-500 mt-1 flex items-center">
                      {isEditing ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isMobileVerified"
                              checked={editableFields.isMobileVerified}
                              onChange={handleInputChange}
                              size="small"
                            />
                          }
                          label="Verified"
                        />
                      ) : editableFields.isMobileVerified ? (
                        <span className="text-green-600 flex items-center">
                          <Check size={14} className="mr-1" /> Verified
                        </span>
                      ) : (
                        <span className="text-yellow-600 flex items-center">
                          <Clock size={14} className="mr-1" /> Not Verified
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Mail
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">Email Address</p>
                    {isEditing ? (
                      <TextField
                        name="email"
                        value={editableFields.email}
                        onChange={handleInputChange}
                        variant="standard"
                        fullWidth
                      />
                    ) : (
                      <p className="text-gray-800">
                        {editableFields.email || "N/A"}
                      </p>
                    )}
                    <div className="text-xs text-gray-500 mt-1 flex items-center">
                      {isEditing ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isEmailVerified"
                              checked={editableFields.isEmailVerified}
                              onChange={handleInputChange}
                              size="small"
                            />
                          }
                          label="Verified"
                        />
                      ) : editableFields.isEmailVerified ? (
                        <span className="text-green-600 flex items-center">
                          <Check size={14} className="mr-1" /> Verified
                        </span>
                      ) : (
                        <span className="text-yellow-600 flex items-center">
                          <Clock size={14} className="mr-1" /> Not Verified
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {kyc && kyc.address && (
                  <div className="flex items-start">
                    <MapPin
                      className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                      size={18}
                    />
                    <div className="flex-grow">
                      <p className="text-sm text-gray-500">Address</p>
                      <p className="text-gray-800 break-words">
                        {kyc.address.line1}, {kyc.address.line2}
                        <br />
                        {kyc.address.city}, {kyc.address.state} -{" "}
                        {kyc.address.pincode}
                        <br />
                        {kyc.address.country}
                      </p>
                    </div>
                  </div>
                )}
                {kyc && kyc.nationality && (
                  <div className="flex items-start">
                    <Flag
                      className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                      size={18}
                    />
                    <div className="flex-grow">
                      <p className="text-sm text-gray-500">Nationality</p>
                      <p className="text-gray-800">{kyc.nationality}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start">
                  <Calendar
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">Account Created On</p>
                    <p className="text-gray-800">
                      {formatDate(user.createdAt)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Last Login: {formatDate(user.lastLogin)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Information */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-800 mb-4">
                Account Information
              </h4>
              <div className="space-y-3">
                {kyc && kyc.dematDetails && (
                  <>
                    <div className="flex items-start">
                      <Briefcase
                        className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                        size={18}
                      />
                      <div className="flex-grow">
                        <p className="text-sm text-gray-500">Client ID</p>
                        <p className="text-gray-800">
                          {kyc.dematDetails.clientId || "Not assigned"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Building
                        className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                        size={18}
                      />
                      <div className="flex-grow">
                        <p className="text-sm text-gray-500">DP ID</p>
                        <p className="text-gray-800">
                          {kyc.dematDetails.dpId || "Not assigned"}
                        </p>
                      </div>
                    </div>
                  </>
                )}
                {kyc && kyc.panNumber && (
                  <div className="flex items-start">
                    <Hash
                      className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                      size={18}
                    />
                    <div className="flex-grow">
                      <p className="text-sm text-gray-500">PAN Number</p>
                      <p className="text-gray-800 font-mono">{kyc.panNumber}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start">
                  <Shield
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">KYC Status</p>
                    {isEditing ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="isKycVerified"
                            checked={editableFields.isKycVerified}
                            onChange={handleInputChange}
                            size="small"
                          />
                        }
                        label="Verified"
                      />
                    ) : (
                      <p
                        className={`font-medium ${
                          editableFields.isKycVerified
                            ? "text-green-600"
                            : "text-yellow-600"
                        }`}
                      >
                        {editableFields.isKycVerified ? "Verified" : "Pending"}
                      </p>
                    )}
                    {kyc && kyc.verificationStatus && (
                      <p className="text-xs text-gray-500 mt-1">
                        Verification: {kyc.verificationStatus}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-start">
                  <CreditCard
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">Account Status</p>
                    {isEditing ? (
                      <Select
                        name="status"
                        value={editableFields.status}
                        onChange={handleInputChange}
                        size="small"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                        <MenuItem value="suspended">Suspended</MenuItem>
                      </Select>
                    ) : (
                      <p
                        className={`font-medium ${
                          editableFields.status === "active"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {editableFields.status
                          ? editableFields.status.charAt(0).toUpperCase() +
                            editableFields.status.slice(1)
                          : "Inactive"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-start">
                  <User
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">User Role</p>
                    <p className="text-gray-800 capitalize">
                      {user.role || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bank Details */}
          {kyc && kyc.bankDetails && (
            <div className="mt-6 border border-gray-200 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-800 mb-4">
                Bank Details
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <Bank
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">Bank Name</p>
                    <p className="text-gray-800">
                      {kyc.bankDetails.bankName || "N/A"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <User
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">Account Name</p>
                    <p className="text-gray-800">
                      {kyc.bankDetails.accountName || "Not provided"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Hash
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">Account Number</p>
                    <p className="text-gray-800 font-mono">
                      {kyc.bankDetails.accountNumber || "N/A"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Hash
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">IFSC Code</p>
                    <p className="text-gray-800 font-mono">
                      {kyc.bankDetails.ifscCode || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Document Information */}
          {kyc && kyc.documents && (
            <div className="mt-6 border border-gray-200 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-800 mb-4">
                Submitted Documents
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-start">
                  <FileText
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">PAN Card</p>
                    <p className="text-gray-800 flex items-center">
                      {kyc.documents.panCard ? (
                        <>
                          <Check size={16} className="text-green-600 mr-1" />
                          Submitted {documentStatusBadge(kyc.documents.panCard)}
                        </>
                      ) : (
                        "Not submitted"
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FileText
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">Aadhaar Card</p>
                    <div className="space-y-1">
                      {kyc.documents.aadhaarFront ? (
                        <p className="text-gray-800 flex items-center text-xs sm:text-sm">
                          <Check size={14} className="text-green-600 mr-1 flex-shrink-0" />
                          <span className="flex-1">Front: Submitted {documentStatusBadge(kyc.documents.aadhaarFront)}</span>
                        </p>
                      ) : null}
                      {kyc.documents.aadhaarBack ? (
                        <p className="text-gray-800 flex items-center text-xs sm:text-sm">
                          <Check size={14} className="text-green-600 mr-1 flex-shrink-0" />
                          <span className="flex-1">Back: Submitted {documentStatusBadge(kyc.documents.aadhaarBack)}</span>
                        </p>
                      ) : null}
                      {!kyc.documents.aadhaarFront && !kyc.documents.aadhaarBack && (
                        <p className="text-gray-800 flex items-center text-xs sm:text-sm">
                          {kyc.documents.aadhaarCard ? (
                            <>
                              <Check size={14} className="text-green-600 mr-1 flex-shrink-0" />
                              <span className="flex-1">Submitted {documentStatusBadge(kyc.documents.aadhaarCard)}</span>
                            </>
                          ) : (
                            "Not submitted"
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <FileText
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">Bank Documents</p>
                    <p className="text-gray-800 flex items-center">
                      {kyc.documents.bankDocuments ? (
                        <>
                          <Check size={16} className="text-green-600 mr-1" />
                          Submitted{" "}
                          {documentStatusBadge(kyc.documents.bankDocuments)}
                        </>
                      ) : (
                        "Not submitted"
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <FileText
                    className="mr-3 text-blue-500 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <div className="flex-grow">
                    <p className="text-sm text-gray-500">Demat Documents</p>
                    <p className="text-gray-800 flex items-center">
                      {kyc.documents.dematDocuments ? (
                        <>
                          <Check size={16} className="text-green-600 mr-1" />
                          Submitted{" "}
                          {documentStatusBadge(kyc.documents.dematDocuments)}
                        </>
                      ) : (
                        "Not submitted"
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Portfolios section */}
       {/* Portfolios section - Fully Responsive */}
<div className="mt-6 border border-gray-200 rounded-lg p-3 sm:p-4 lg:p-6">
  <h4 className="text-base sm:text-lg font-medium text-gray-800 mb-3 sm:mb-4">
    Portfolios
  </h4>
  {userData.data.portfolios && userData.data.portfolios.length > 0 ? (
    <div className="space-y-3 sm:space-y-4">
      {userData.data.portfolios.map((portfolio, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-white hover:bg-gray-50 transition-colors"
        >
          {/* Portfolio Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4 mb-3">
            <h5 className="font-medium text-sm sm:text-base text-gray-900 break-words">
              {portfolio.portfolioName}
            </h5>
            <span
              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap self-start sm:self-center ${
                portfolio.status === "ACTIVE"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {portfolio.status}
            </span>
          </div>
          
          {/* Portfolio Details Grid - Responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-3">
            <div className="flex flex-col">
              <span className="text-xs sm:text-sm text-gray-500 font-medium">Type</span>
              <span className="text-xs sm:text-sm text-gray-900 break-words">
                {portfolio.portfolioType}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs sm:text-sm text-gray-500 font-medium">Risk Profile</span>
              <span className="text-xs sm:text-sm text-gray-900">
                {portfolio.riskProfile}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs sm:text-sm text-gray-500 font-medium">Strategy</span>
              <span className="text-xs sm:text-sm text-gray-900 break-words">
                {portfolio.investmentStrategy}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs sm:text-sm text-gray-500 font-medium">Created</span>
              <span className="text-xs sm:text-sm text-gray-900">
                {formatDate(portfolio.createdAt)}
              </span>
            </div>
          </div>
          
          {/* Portfolio Summary - Enhanced Responsive Design */}
          {portfolio.summary && (
            <div className="mt-3 p-3 sm:p-4 bg-gray-50 rounded-lg border">
              <h6 className="text-xs sm:text-sm font-semibold mb-2 sm:mb-3 text-gray-900">
                Portfolio Summary
              </h6>
              
              {/* Summary Grid - Mobile First Approach */}
              <div className="space-y-2 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-3 lg:gap-4">
                <div className="flex justify-between sm:flex-col sm:justify-start">
                  <span className="text-xs sm:text-sm text-gray-600 font-medium">
                    Investment:
                  </span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">
                    ₹{portfolio.summary.totalInvestment.toLocaleString('en-IN')}
                  </span>
                </div>
                
                <div className="flex justify-between sm:flex-col sm:justify-start">
                  <span className="text-xs sm:text-sm text-gray-600 font-medium">
                    Current Value:
                  </span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">
                    ₹{portfolio.summary.currentValue.toLocaleString('en-IN')}
                  </span>
                </div>
                
                <div className="flex justify-between sm:flex-col sm:justify-start">
                  <span className="text-xs sm:text-sm text-gray-600 font-medium">
                    Profit/Loss:
                  </span>
                  <span
                    className={`text-xs sm:text-sm font-semibold ${
                      portfolio.summary.totalProfit >= 0
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {portfolio.summary.totalProfit >= 0 ? "+" : ""}₹
                    {Math.abs(portfolio.summary.totalProfit).toLocaleString('en-IN')}
                  </span>
                </div>
                
                <div className="flex justify-between sm:flex-col sm:justify-start">
                  <span className="text-xs sm:text-sm text-gray-600 font-medium">
                    Return:
                  </span>
                  <span
                    className={`text-xs sm:text-sm font-semibold ${
                      portfolio.summary.profitPercentage >= 0
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {portfolio.summary.profitPercentage >= 0 ? "+" : ""}
                    {portfolio.summary.profitPercentage}%
                  </span>
                </div>
              </div>
              
              {/* Last Updated Info */}
              <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-500 text-center sm:text-left">
                  <span className="font-medium">Last updated:</span>{" "}
                  <span>{formatDate(portfolio.summary.lastValuationDate)}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Additional Portfolio Actions (if needed) */}
          <div className="mt-3 flex flex-wrap gap-2 justify-end">
            {/* Add any portfolio-specific action buttons here if needed */}
            {/* Example:
            <button className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors">
              View Details
            </button>
            */}
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="text-center py-8 sm:py-12">
      <div className="text-gray-400 mb-2">
        <Briefcase size={32} className="mx-auto sm:w-10 sm:h-10" />
      </div>
      <p className="text-sm sm:text-base text-gray-600">No portfolios available</p>
      <p className="text-xs sm:text-sm text-gray-500 mt-1">
        Portfolios will appear here once created
      </p>
    </div>
  )}
</div>

          {/* Action buttons */}
          <div className="mt-6 flex flex-wrap justify-end gap-2">
            {isEditing ? (
              <>
                <Button
                  onClick={cancelEdit}
                  variant="outlined"
                  disabled={isSubmitting}
                  startIcon={<X size={16} />}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={<Save size={16} />}
                >
                  {isSubmitting ? "Processing..." : "Save Changes"}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={openReportModal}
                  variant="outlined"
                  startIcon={<Download size={16} />}
                >
                  Download Report
                </Button>
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="contained"
                  startIcon={<Edit size={16} />}
                >
                  Edit User
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Report Modal */}
        {showReportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-medium">User Reports</h3>
                <button
                  onClick={closeReportModal}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-4">
                <UserReportsTab
                  userData={userData}
                  onDownloadSuccess={handleReportDownloadSuccess}
                  onDownloadError={handleReportDownloadError}
                />
              </div>
            </div>
          </div>
        )}

        {/* Custom CSS for toast animation */}
        <style jsx>{`
          @keyframes slide-in-right {
            0% {
              transform: translateX(100%);
              opacity: 0;
            }
            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }

          .animate-slide-in-right {
            animation: slide-in-right 0.3s ease-out;
          }
        `}</style>
      </div>
    </>
  );
};

export default UserDetailView;